import axios from 'axios'
import authService from './services/auth.service'
import moment from 'moment'
import {
  getQueueSessionId,
  getWaitingRoomToken,
  getCurrentWaitingRoomEventUuid,
  clearWaitingRoomToken,
  setWaitingRoomReturnPath,
} from './waitingRoom'

const instance = axios.create({
  baseURL: process.env.REACT_APP_API,
  timeout: 30000,
  withCredentials: false,
})

const GATED_URL_PATTERNS = [
  /\/events\/seatmap\//,
  /\/tickets\/available/,
  /\/events\/place-tickets-into-cart/,
  /\/events\/place-ga-tickets-into-cart/,
  /\/events\/place-package-into-cart/,
  /\/access-pass-templates\/add-to-cart/,
  /\/flex-pack\/add-to-cart/,
]

function attachWaitingRoomHeaders(config) {
  const url = config.url || ''
  const isGated = GATED_URL_PATTERNS.some((pattern) => pattern.test(url))
  if (!isGated) return config

  config.headers['X-Queue-Session-Id'] = getQueueSessionId()

  const eventUuid =
    config.params?.eventUUID ||
    config.params?.eventUuid ||
    config.data?.eventUUID ||
    config.data?.eventUuid ||
    config.data?.data?.eventUUID ||
    config.data?.data?.eventUuid ||
    getCurrentWaitingRoomEventUuid()

  const token = getWaitingRoomToken(eventUuid)
  if (token) {
    config.headers['X-Waiting-Room-Token'] = token
  }

  return config
}

// Add a request interceptor
instance.interceptors.request.use(
  function (config) {
    // Do something before request is sent
    const isLoggedIn = authService?.isLoggedIn()
    const jwt = isLoggedIn ? authService.token() : null
    if (jwt) {
      config.headers['Authorization'] = `Bearer ${jwt}`
    }
    if ((config.url || '').includes('/waiting-room/')) {
      config.headers['X-Queue-Session-Id'] = getQueueSessionId()
    }
    return attachWaitingRoomHeaders(config)
  },
  function (error) {
    // Do something with request errors
    console.error('API Error:', error?.response?.data || error)
    return Promise.reject(error)
  }
)

instance.interceptors.response.use(
  (response) => response,
  (error) => {
    const original = error?.config
    const status = error?.response?.status
    const sentAuth = Boolean(
      original?.headers?.Authorization || original?.headers?.authorization
    )
    // A stale session token makes Strapi reject public browse calls. Drop it
    // and retry once so on-sale events, venues, and organizations can load.
    const method = String(original?.method || 'get').toLowerCase()
    const canRetryWithoutAuth = method === 'get' || method === 'head'
    if (
      status === 401 &&
      sentAuth &&
      original &&
      canRetryWithoutAuth &&
      !original._retriedWithoutAuth
    ) {
      authService.logoutUser()
      original._retriedWithoutAuth = true
      if (typeof original.headers?.delete === 'function') {
        original.headers.delete('Authorization')
      } else if (original.headers) {
        delete original.headers.Authorization
        delete original.headers.authorization
      }
      return instance(original)
    }

    const data = error?.response?.data
    if (data?.code === 'WAITING_ROOM_REQUIRED') {
      if (data.eventUuid) {
        clearWaitingRoomToken(data.eventUuid)
        const returnPath =
          typeof window !== 'undefined'
            ? `${window.location.pathname}${window.location.search || ''}`
            : null
        if (returnPath && !returnPath.includes('/waiting-room')) {
          setWaitingRoomReturnPath(data.eventUuid, returnPath)
        }
      }
      const joinUrl = data.joinUrl
      const onWaitingRoom =
        typeof window !== 'undefined' &&
        window.location?.pathname?.includes('/waiting-room')
      if (joinUrl && !onWaitingRoom && typeof window !== 'undefined') {
        window.location.assign(joinUrl)
      }
    }
    return Promise.reject(error)
  }
)

export const verifyUser = (data) => {
  return instance.post('/verifies', data)
}

export const verifiyCode = (data) => {
  return instance.post('/verifies/by-phone-or-email', data)
}

export const phoneUnique = (data) => {
  return instance.post('/verifies/phone-unique', data)
}

export const createNewUser = (data) => {
  return instance.post('/verifies/newUser', data)
}

export const updateSmsOptIn = (data) => {
  return instance.post('/verifies/sms-opt-in', data)
}

export const createOrder = (data) => {
  return instance.post('/orders', data)
}

export const processOrder = (data) => {
  return instance.post('/orders/process', data)
}

export const processFreeOrder = (data) => {
  return instance.post('/orders/process-free', data)
}

export const getPricing = (data) => {
  return instance.post('/orders/pricing', data)
}

export const getOrder = (orderId) => {
  return instance.get(`/orders?filters[orderId][$eq]=${orderId}`)
}

export const getAccessPassesByOrder = (orderId) => {
  return instance.get(`/access-passes/by-order/${orderId}`)
}

export const getMyAccessPasses = (type = 'organizer') => {
  return instance.get('/events/myAccessPasses', {
    params: type ? { type } : {},
  })
}

export const getMyAccessPass = (uuid) => {
  return instance.get(`/events/myAccessPasses/${uuid}`)
}

export const findMadeOrder = (data) => {
  return instance.post('/orders/findMadeOrder', data)
}

export const getMyOrganizations = async () => {
  return instance.get('/organizations/myOrgs')
}

export const createOrganization = async (data) => {
  return instance.post('/organizations', data)
}

export const getPaymentIntent = async (data) => {
  return instance.post(`/payment/intent`, data)
}

export const resolveFundraisingCampaign = ({
  organizationUUID,
  eventUUID,
  packageUUID,
  flexPackUUID,
  participantUuid,
  sectionIds,
} = {}) => {
  const params = new URLSearchParams()
  params.set('organizationUUID', organizationUUID)
  if (eventUUID) params.set('eventUUID', eventUUID)
  if (packageUUID) params.set('packageUUID', packageUUID)
  if (flexPackUUID) params.set('flexPackUUID', flexPackUUID)
  if (participantUuid) params.set('participantUuid', participantUuid)
  if (sectionIds) params.set('sectionIds', sectionIds)
  return instance.get(`/fundraising-campaigns/resolve?${params.toString()}`)
}

export const getPublicFundraisingCampaign = (
  slug,
  { organizationUUID, organizationSlug } = {}
) => {
  const params = new URLSearchParams()
  if (organizationUUID) params.set('organizationUUID', organizationUUID)
  if (organizationSlug) params.set('organizationSlug', organizationSlug)
  const query = params.toString()
  return instance.get(
    `/fundraising-campaigns/public/${encodeURIComponent(slug)}${
      query ? `?${query}` : ''
    }`
  )
}

export const createLandingPageDonationIntent = (slug, payload) =>
  instance.post(
    `/fundraising-campaigns/public/${encodeURIComponent(slug)}/donate`,
    payload
  )

export const confirmLandingPageDonation = (paymentIntentId) =>
  instance.post('/fundraising-campaigns/donate/confirm', { paymentIntentId })

export const getCategories = async () => {
  return instance.get('/categories')
}

export const getVenues = async () => {
  return instance.get('/venues/find-on-sale')
}

export const getOrganizationsOnSale = async () => {
  return instance.get('/organizations/on-sale')
}

export const getGuestPasses = async (eventId, phoneNumber) => {
  return instance.get(
    `/guest-passes?filters[$and][0][eventId][$eq]=${eventId}&filters[$and][1][phoneNumber][$contains]=${phoneNumber}`
  )
}

export const getGuestList = async (phoneNumber) => {
  return instance.get(`/guest-lists?phoneNumber=${phoneNumber}`)
}

export const getVenue = async (id) => {
  return instance.get(`/venues?filters[slug][$eq]=${id}`)
}

export const getVenueUpcomingEvents = async (id) => {
  return instance.get(`/venues/${id}/upcoming-events`)
}

export const getOrganizationStorefront = async (slug) => {
  return instance.get(`/organizations/storefront/${slug}`)
}

export const getEvents = async () => {
  return instance.get(`/events/on-sale`)
}

export const getTaxRates = async (city, state) => {
  return instance.get(`organizations/tax-rates?city=${city}&state=${state}`)
}

export const searchEvents = async (q) => {
  return instance.post(`/events/search`, q)
}

export const getMyEvents = async () => {
  return instance.get('/events/myUpcomingEvents')
}

export const getMyUpcomingOrders = async () => {
  return instance.get('/events/myUpcomingOrders')
}

export const getEvent = async (id) => {
  return instance.get(`/events/${id}?filters[status][$eq]=on_sale`)
}

export const getEventByShortCode = async (id, slug, code) => {
  return instance.get(`/events/${slug}/${id}?code=${code}`)
}

export const getSeatmapByShortCode = async (id, slug) => {
  return instance.get(`/events/seatmap/${slug}/${id}`)
}

export const getEventTickets = async (id) => {
  let date = moment().toISOString()
  return instance.get(
    `/tickets?filters[eventId][$eq]=${id}&filters[on_sale_status][$eq]=available&filters[sales_start][$lte]=${date}&filters[sales_end][$gte]=${date}`
  )
}

export const getAllEventTickets = async (id, code) => {
  return instance.get(`/tickets/available?eventUUID=${id}&code=${code}`)
}

export const createTicketTransfer = (data) => {
  return instance.post('/ticket-transfers', data)
}

export const updatePersonalDetails = (data) => {
  return instance.post('/verifies/personalDetails', data)
}

export const createBankAccount = (data) => {
  return instance.post('/payment-information/generate', data)
}

export const getBankAccount = () => {
  return instance.get('/payment-informations/0')
}

// TEST APPLE/GOOGLE WALLET
export const savePassApple = () => {
  return instance.get('/aaa/apple', {
    responseType: 'blob',
  })
}

export const savePassGoogle = () => {
  return instance.get('/aaa/google')
}
// END TEST APPLE/GOOGLE WALLET

export const removeBankAccount = () => {
  return instance.get('/payment-information/deactive')
}

export const getMySentTransfers = (userEmail, page) => {
  return instance.get(
    `/ticket-transfers?filters[fromUserEmail][$eq]=${userEmail}&populate=*&sort[0]=createdAt
:desc&pagination[page]=${page}&pagination[pageSize]=50`
  )
}

export const getMyReceivedTransfers = (userEmail, page) => {
  return instance.get(
    `/ticket-transfers?filters[emailAddressToUser][$eq]=${userEmail}&populate=*&sort[0]=createdAt
:desc&pagination[page]=${page}&pagination[pageSize]=50`
  )
}

export const cancelMyTransfers = (data) => {
  return instance.post('/ticket-transfers/cancel', data)
}

export const getIncomingTransfers = () => {
  return instance.get('/ticket-transfers/incoming')
}

export const acceptIncomingTransfers = (data) => {
  return instance.post('/ticket-transfers/accept', data)
}

export const createListing = (data) => {
  return instance.post('/listings', data)
}

export const getListingsByEvent = (id) => {
  return instance.get(`/listings/byEvent?id=${id}`)
}

export const getMyListings = () => {
  return instance.get('/listings/mylisting')
}

export const getResaleFeeRules = (eventId) => {
  return instance.get(`/listings/resale-fee-rules?eventId=${eventId}`)
}

export const getAvailableFunds = () => {
  return instance.get('/listings/available-funds')
}

export const claimResaleFunds = () => {
  return instance.post('/listings/claim-funds')
}

export const removeMyListings = (id) => {
  return instance.delete(`/listings/${id}`)
}

export const updateMyListings = (id, data) => {
  return instance.put(`/listings/${id}`, data)
}

export const getResaleTickets = (eventId) => {
  return instance.get(
    `/tickets?filters[eventId][$eq]=${eventId}&filters[on_sale_status][$eq]=resaleAvailable`
  )
}

// check if email exists in system
export const validEmail = (data) => {
  return instance.post('/verifies/emailValid', data)
}

export const requestNumberChange = (data) => {
  return instance.post('/verifies/change-number', data)
}

export const updateNumber = (data) => {
  return instance.post('/verifies/confirm-update', data)
}

export const confirmCode = (data) => {
  return instance.post('/tickets/unlock', data)
}

export const checkAccessCode = (data) => {
  return instance.post('/tickets/checkAccessCode', data)
}

export const getOffers = (eventId) => {
  return instance.get(`/events/offers?uuid=${eventId}`)
}

export const getTicketsFromOffer = (offerId, eventId) => {
  return instance.get(
    `/events/offer-tickets?uuid=${eventId}&offerUUID=${offerId}`
  )
}

export const placeTicketsIntoCart = (data) => {
  return instance.post(`/events/place-tickets-into-cart`, data)
}

export const placeGATicketsIntoCart = (data) => {
  return instance.post(`/events/place-ga-tickets-into-cart`, data)
}

export const placePackageIntoCart = (data) => {
  return instance.post(`/events/place-package-into-cart`, data)
}

export const getTicketGroups = (data) => {
  return instance.post(`/ticket-group/get-ticket-groups`, data)
}

export const getCart = (cartId) => {
  return instance.get(`/cart/myCart?cartId=${cartId}`)
}

export const dropUserCart = (data) => {
  return instance.post(`/cart/drop-user-cart`, data)
}

export const getVenuePackages = (venueUUID) => {
  return instance.get(`/packages/get-venue-packages?venueUUID=${venueUUID}`)
}

export const getPackageFE = (uuid) => {
  return instance.get(`/packages/get-package-fe?uuid=${uuid}`)
}

export const getMyPackage = (uuid) => {
  return instance.get(`/packages/get-my-package?uuid=${uuid}`)
}

export const downloadApplePass = async (data) => {
  return instance.post(`/download_apple_pass`, data, { responseType: 'blob' })
}

export const downloadGooglePass = async (data) => {
  return instance.post('/create_download_google_pass', data)
}

export const getSmsTickets = (token) => {
  return instance.get(`/orders/sms-tickets/${encodeURIComponent(token)}`)
}

// validate email against SendGrid
export const validateEmail = async (data) => {
  return instance.post('/registration/validate-email', data)
}

export const getTicketsByEvent = async (uuid) => {
  return instance.get(`/event/my-tickets/${uuid}`)
}

export const getVenueFlexPacks = (venueUUID) => {
  return instance.get(`/flex-pack/get-venue-flex-packs?venueUUID=${venueUUID}`)
}

export const getFlexPack = async (uuid) => {
  return instance.get(`/flex-pack/get-flex-pack?uuid=${uuid}`)
}

export const placeFlexPackIntoCart = async (flexPackId) => {
  return instance.post(`/flex-pack/add-to-cart?id=${flexPackId}`)
}

export const getVenueAccessPasses = (venueUUID) => {
  return instance.get(
    `/access-pass-templates/get-venue-access-passes?venueUUID=${venueUUID}`
  )
}

export const getAccessPassTemplate = async (uuid) => {
  return instance.get(`/access-pass-templates/get-access-pass?uuid=${uuid}`)
}

export const placeAccessPassIntoCart = async (templateId, quantity = 1) => {
  return instance.post(`/access-pass-templates/add-to-cart?id=${templateId}`, {
    quantity,
  })
}

export const trackCartSessionEvent = (data) => {
  return instance.post('/cart-sessions/track', data)
}

export const completeCartSession = (data) => {
  return instance.post('/cart-sessions/complete', data)
}

export const getGroupInvitation = async (groupCode) => {
  return instance.get(
    `/group-purchase-invitations?populate=*&filters[groupCode][$eq]=${groupCode}`
  )
}

export const redeemPromoCode = async (data) => {
  return instance.post(`/promo-code/redeem`, data)
}

export const redeemFlexTicketsAtCheckout = async (data) => {
  return instance.post('/flex-pack/redeem-at-checkout', data)
}

export const removePromoCode = async (data) => {
  return instance.post(`/promo-code/remove`, data)
}

export const getOrderByPaymentIntentId = async (paymentIntentId) => {
  return instance.get(
    `/orders/get-order-by-payment-intent-id?paymentIntentId=${paymentIntentId}`
  )
}

export const joinWaitingRoom = (eventUuid) => {
  return instance.post('/waiting-room/join', { data: { eventUuid } })
}

export const getWaitingRoomStatus = (eventUuid) => {
  return instance.get(`/waiting-room/status?eventUuid=${eventUuid}`)
}

export const heartbeatWaitingRoom = (eventUuid, token) => {
  return instance.post('/waiting-room/heartbeat', {
    data: { eventUuid, token },
  })
}

export const leaveWaitingRoom = (eventUuid, token) => {
  return instance.post('/waiting-room/leave', { data: { eventUuid, token } })
}

/**
 * Best-effort leave for tab close / refresh. Uses fetch keepalive because
 * sendBeacon cannot set X-Queue-Session-Id (required to identify the queue seat).
 */
export const beaconLeaveWaitingRoom = (eventUuid, token, { soft } = {}) => {
  if (!eventUuid || typeof fetch === 'undefined') return
  const baseURL = process.env.REACT_APP_API
  if (!baseURL) return

  const body = JSON.stringify({
    data: {
      eventUuid,
      ...(token ? { token } : {}),
      ...(soft ? { soft: true } : {}),
    },
  })

  try {
    fetch(`${baseURL}/waiting-room/leave`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Queue-Session-Id': getQueueSessionId(),
        ...(token ? { 'X-Waiting-Room-Token': token } : {}),
      },
      body,
      keepalive: true,
      credentials: 'omit',
    }).catch(() => {})
  } catch {
    // Ignore unload failures; cron/TTL still reclaim abandoned slots.
  }
}

export const isWaitingRoomRequiredError = (error) => {
  return error?.response?.data?.code === 'WAITING_ROOM_REQUIRED'
}

export const getPublicMenu = async (
  organizationUuid,
  sectionName,
  rowName,
  seatName,
  { venueUuid, eventUuid } = {}
) => {
  const section = encodeURIComponent(sectionName)
  const row = encodeURIComponent(rowName)
  const seat = encodeURIComponent(seatName)
  const params = {}
  if (venueUuid) params.venueUuid = venueUuid
  if (eventUuid) params.eventUuid = eventUuid
  return instance.get(
    `/fnb-items/public-menu/${organizationUuid}/${section}/${row}/${seat}`,
    { params }
  )
}

export const getEventMenuContext = async (
  eventUuid,
  { section, row, seat } = {}
) => {
  const params = {}
  if (section) params.section = section
  if (row) params.row = row
  if (seat) params.seat = seat
  return instance.get(`/fnb-items/event-menu-context/${eventUuid}`, { params })
}

export const submitPublicMenuOrder = async (payload) => {
  return instance.post('/fnb-items/public-order', { data: payload })
}

export const createPublicMenuPaymentIntent = async (payload) => {
  return instance.post('/fnb-items/public-payment-intent', { data: payload })
}

export const joinWaitlist = (data) => {
  return instance.post('/waitlist/join', data)
}

export const getWaitlistClaim = (token) => {
  return instance.get(`/waitlist/claim/${encodeURIComponent(token)}`)
}

export const claimWaitlist = (token) => {
  return instance.post(`/waitlist/claim/${encodeURIComponent(token)}`)
}

export const getPublicMenuPricing = async (payload) => {
  return instance.post('/fnb-items/public-pricing', { data: payload })
}

export const getPublicOrderStatus = async (
  organizationUuid,
  orderNumber,
  { section, row, seat, venueUuid, eventUuid } = {}
) => {
  const params = {}
  if (section) params.section = section
  if (row) params.row = row
  if (seat) params.seat = seat
  if (venueUuid) params.venueUuid = venueUuid
  if (eventUuid) params.eventUuid = eventUuid
  return instance.get(
    `/fnb-orders/public-status/${organizationUuid}/${encodeURIComponent(orderNumber)}`,
    { params }
  )
}
